import pino from 'pino';
// Only import os conditionally to avoid edge runtime errors
import { LogFormatter } from './utils/logFormatter';

type LogParams = Record<string, any>;

interface LogOptions {
  isFullLog?: boolean;
}

// Check if we're in Edge Runtime
const isEdgeRuntime = typeof process !== 'undefined' && 
  process.env.NEXT_RUNTIME === 'edge';

// Define a minimal logger interface that matches what we need
interface MinimalLogger {
  info: (obj: any, msg?: string) => void;
  error: (obj: any, msg?: string) => void;
  warn: (obj: any, msg?: string) => void;
  debug: (obj: any, msg?: string) => void;
  child: (bindings: Record<string, any>) => MinimalLogger;
}

// Function to get base options ONLY in Node.js environment
const getNodeJsBaseOptions = () => {
  // Removed os and process.pid usage to ensure Edge compatibility
  return {}; 
};

// Create logger factory based on environment
function createLogger(): MinimalLogger {
  // For Edge Runtime, create a console-based logger
  if (isEdgeRuntime) {
    return {
      info: (obj: any, msg?: string) => console.log(msg || '', obj),
      error: (obj: any, msg?: string) => console.error(msg || '', obj),
      warn: (obj: any, msg?: string) => console.warn(msg || '', obj),
      debug: (obj: any, msg?: string) => console.debug(msg || '', obj),
      child: () => createLogger(),
    };
  }
  
  // For Node.js environment, create a pino logger
  try {
    return pino({
      level: process.env.NODE_ENV === 'development' ? 'debug' : 'info',
      formatters: {
        level: (label) => {
          return { level: label.toUpperCase() };
        },
      },
      timestamp: pino.stdTimeFunctions.isoTime,
      serializers: {
        error: pino.stdSerializers.err,
      },
      base: getNodeJsBaseOptions(), // Get base options safely
    });
  } catch (e) {
    // Fallback if pino fails to initialize (should be rare)
    console.error('[Logger] Failed to initialize Pino, falling back to console:', e);
    return {
      info: (obj: any, msg?: string) => console.log(msg || '', obj),
      error: (obj: any, msg?: string) => console.error(msg || '', obj),
      warn: (obj: any, msg?: string) => console.warn(msg || '', obj),
      debug: (obj: any, msg?: string) => console.debug(msg || '', obj),
      child: () => createLogger(),
    };
  }
}

class StructuredLogger {
  private logger: MinimalLogger;
  private defaultOptions: LogOptions = {
    isFullLog: false
  };

  constructor(baseLogger?: MinimalLogger, options?: LogOptions) {
    this.logger = baseLogger || createLogger();
  }

  // Support for child loggers with additional context
  child(bindings: Record<string, any>): StructuredLogger {
    return new StructuredLogger(this.logger.child(bindings));
  }

  private isError(value: unknown): value is Error {
    return value instanceof Error || (
      typeof value === 'object' && 
      value !== null && 
      'message' in value &&
      'name' in value
    );
  }

  private normalizeError(error: unknown): Error {
    if (this.isError(error)) {
      return error;
    }
    if (typeof error === 'string') {
      return new Error(error);
    }
    return new Error(JSON.stringify(error));
  }

  private formatValue(value: unknown): string {
    if (typeof value === 'object' && value !== null) {
      try {
        return '\n' + JSON.stringify(value, null, 2);
      } catch (err) {
        return String(value);
      }
    }
    // Try to parse string if it looks like JSON to avoid double escaping
    if (typeof value === 'string' && 
       ((value.startsWith('{') && value.endsWith('}')) || 
        (value.startsWith('[') && value.endsWith(']')))) {
      try {
        const parsed = JSON.parse(value);
        return '\n' + JSON.stringify(parsed, null, 2);
      } catch (e) {
        // Not valid JSON, return as is
      }
    }
    return String(value);
  }

  private normalizeArgs(...args: unknown[]): { 
    message: string; 
    params: LogParams;
  } {
    // If no arguments, return empty
    if (args.length === 0) {
      return { message: '', params: {} };
    }

    // Handle single argument cases
    if (args.length === 1) {
      const [arg] = args;
      if (typeof arg === 'string') {
        return { message: arg, params: {} };
      }
      if (typeof arg === 'object' && arg !== null) {
        return { message: '', params: arg as LogParams };
      }
      return { message: String(arg), params: {} };
    }

    // Handle multiple arguments
    const firstArg = args[0];
    const secondArg = args[1];
    const remainingArgs = args.slice(2);

    // Case: logger.info('message', { params })
    if (typeof firstArg === 'string' && typeof secondArg === 'object' && secondArg !== null && !Array.isArray(secondArg) && !this.isError(secondArg) && remainingArgs.length === 0) {
      return { message: firstArg, params: secondArg as LogParams };
    }

    // Case: logger.info({ params }, 'message')
    if (typeof firstArg === 'object' && firstArg !== null && !Array.isArray(firstArg) && !this.isError(firstArg) && typeof secondArg === 'string' && remainingArgs.length === 0) {
      return { message: secondArg, params: firstArg as LogParams };
    }

    // Case: logger.debug('Capturing event:', eventName, properties)
    // Convert additional arguments into params
    if (typeof firstArg === 'string') {
      const params: LogParams = {};
      const allArgs = [secondArg, ...remainingArgs];
      
      allArgs.forEach((arg, index) => {
        if (typeof arg === 'object' && arg !== null && !Array.isArray(arg)) {
          Object.assign(params, arg);
        } else {
          params[`arg${index + 1}`] = this.formatValue(arg);
        }
      });

      return { message: firstArg, params };
    }

    // Default case - convert everything to params
    const params: LogParams = {};
    args.forEach((arg, index) => {
      if (typeof arg === 'object' && arg !== null && !Array.isArray(arg)) {
        Object.assign(params, arg);
      } else {
        params[`arg${index}`] = arg;
      }
    });

    return { message: '', params };
  }

  private formatLog(logEntry: Record<string, any>, options?: LogOptions): string {
    const finalOptions = { ...this.defaultOptions, ...options };
    return LogFormatter.formatToString(logEntry, finalOptions.isFullLog);
  }

  info(...args: unknown[]) {
    const { message, params } = this.normalizeArgs(...args);
    // Extract potential request/chat ID for tracking
    const reqId = params.id || params.chatId || params.requestId || null;

    // Log using the MinimalLogger interface
    this.logger.info(
      { 
        ...params,
        ...(reqId ? { reqId } : {})
      }, 
      message
    );

    // In development, also log a formatted version to console (not for Edge Runtime)
    if (!isEdgeRuntime && process.env.NODE_ENV === 'development') {
      const time = new Date().toLocaleTimeString();
      const coloredTime = `\x1b[90m[${time}]\x1b[0m`;
      const coloredLevel = `\x1b[32mINFO\x1b[0m`;
      const formattedMessage = message ? `\x1b[1m${message}\x1b[0m` : '';
      const reqIdStr = reqId ? `\x1b[36m(${reqId})\x1b[0m ` : '';
      
      let details = '';
      if (Object.keys(params).length > 0) {
        try {
          // Show a limited view of params in console
          const cleanParams = { ...params };
          if (reqId) delete cleanParams.reqId;
          if (cleanParams.id === reqId) delete cleanParams.id;
          
          details = Object.keys(cleanParams).length > 0 
            ? `\n${JSON.stringify(cleanParams, null, 2)}`
            : '';
        } catch (e) {
          details = '\n[Could not stringify params]';
        }
      }
      
      console.log(`${coloredTime} ${coloredLevel}: ${reqIdStr}${formattedMessage}${details}`);
    }
  }

  error(...args: unknown[]) {
    let { message, params } = this.normalizeArgs(...args);
    
    const error = args.find(arg => this.isError(arg));
    if (error) {
      const normalizedError = this.normalizeError(error);
      params = {
        ...params,
        err: normalizedError,
      };
    }

    // Extract potential request/chat ID for tracking
    const reqId = params.id || params.chatId || params.requestId || null;

    // Log using the MinimalLogger interface
    this.logger.error(
      { 
        ...params,
        ...(reqId ? { reqId } : {})
      }, 
      message
    );
    
    // In development, also log a formatted version to console (not for Edge Runtime)
    if (!isEdgeRuntime && process.env.NODE_ENV === 'development') {
      const time = new Date().toLocaleTimeString();
      const coloredTime = `\x1b[90m[${time}]\x1b[0m`;
      const coloredLevel = `\x1b[31mERROR\x1b[0m`;
      const formattedMessage = message ? `\x1b[1m${message}\x1b[0m` : '';
      const reqIdStr = reqId ? `\x1b[36m(${reqId})\x1b[0m ` : '';
      
      // Format error details
      let details = '';
      if (error) {
        const err = this.normalizeError(error);
        const stack = err.stack ? err.stack.split('\n').map((line, i) => {
          if (i === 0) return `\x1b[31m${line}\x1b[0m`;
          return `\x1b[90m  ${line}\x1b[0m`;
        }).join('\n') : '';
        
        details = `\n\x1b[31mError: ${err.message}\x1b[0m\n${stack}`;
      } else if (Object.keys(params).length > 0) {
        try {
          const cleanParams = { ...params };
          if (reqId) delete cleanParams.reqId;
          if (cleanParams.id === reqId) delete cleanParams.id;
          
          details = Object.keys(cleanParams).length > 0 
            ? `\n${JSON.stringify(cleanParams, null, 2)}`
            : '';
        } catch (e) {
          details = '\n[Could not stringify params]';
        }
      }
      
      console.error(`${coloredTime} ${coloredLevel}: ${reqIdStr}${formattedMessage}${details}`);
    }
  }

  warn(...args: unknown[]) {
    const { message, params } = this.normalizeArgs(...args);
    // Extract potential request/chat ID for tracking
    const reqId = params.id || params.chatId || params.requestId || null;

    // Log using the MinimalLogger interface
    this.logger.warn(
      { 
        ...params,
        ...(reqId ? { reqId } : {})
      }, 
      message
    );
    
    // Additional console formatting only for Node.js environment
    if (!isEdgeRuntime && process.env.NODE_ENV === 'development') {
      const time = new Date().toLocaleTimeString();
      const coloredTime = `\x1b[90m[${time}]\x1b[0m`;
      const coloredLevel = `\x1b[33mWARN\x1b[0m`;
      const formattedMessage = message ? `\x1b[1m${message}\x1b[0m` : '';
      const reqIdStr = reqId ? `\x1b[36m(${reqId})\x1b[0m ` : '';
      
      let details = '';
      if (Object.keys(params).length > 0) {
        try {
          const cleanParams = { ...params };
          if (reqId) delete cleanParams.reqId;
          if (cleanParams.id === reqId) delete cleanParams.id;
          
          details = Object.keys(cleanParams).length > 0 
            ? `\n${JSON.stringify(cleanParams, null, 2)}`
            : '';
        } catch (e) {
          details = '\n[Could not stringify params]';
        }
      }
      
      console.warn(`${coloredTime} ${coloredLevel}: ${reqIdStr}${formattedMessage}${details}`);
    }
  }

  debug(...args: unknown[]) {
    const { message, params } = this.normalizeArgs(...args);
    // Extract potential request/chat ID for tracking
    const reqId = params.id || params.chatId || params.requestId || null;

    // Log using the MinimalLogger interface
    this.logger.debug(
      { 
        ...params,
        ...(reqId ? { reqId } : {})
      }, 
      message
    );
    
    // Additional console formatting only for Node.js environment
    if (!isEdgeRuntime && process.env.NODE_ENV === 'development') {
      const time = new Date().toLocaleTimeString();
      const coloredTime = `\x1b[90m[${time}]\x1b[0m`;
      const coloredLevel = `\x1b[36mDEBUG\x1b[0m`;
      const formattedMessage = message ? `\x1b[1m${message}\x1b[0m` : '';
      const reqIdStr = reqId ? `\x1b[36m(${reqId})\x1b[0m ` : '';
      
      let details = '';
      if (Object.keys(params).length > 0) {
        try {
          const cleanParams = { ...params };
          if (reqId) delete cleanParams.reqId;
          if (cleanParams.id === reqId) delete cleanParams.id;
          
          details = Object.keys(cleanParams).length > 0 
            ? `\n${JSON.stringify(cleanParams, null, 2)}`
            : '';
        } catch (e) {
          details = '\n[Could not stringify params]';
        }
      }
      
      console.debug(`${coloredTime} ${coloredLevel}: ${reqIdStr}${formattedMessage}${details}`);
    }
  }

  // Development only console methods
  devLog(...args: unknown[]) {
    if (process.env.NODE_ENV === 'development') {
      const { message, params } = this.normalizeArgs(...args);
      this.logger.debug({ ...params, dev: true }, `[DEV] ${message}`);
    }
  }

  devError(...args: unknown[]) {
    if (process.env.NODE_ENV === 'development') {
      const { message, params } = this.normalizeArgs(...args);
      const error = args.find(arg => this.isError(arg));
      if (error) {
        const normalizedError = this.normalizeError(error);
        this.logger.error({ ...params, err: normalizedError, dev: true }, `[DEV] ${message}`);
      } else {
        this.logger.error({ ...params, dev: true }, `[DEV] ${message}`);
      }
    }
  }

  // Create a logger with request context
  request(req: any): StructuredLogger {
    const requestId = req.headers?.['x-request-id'] || 
                      req.headers?.['request-id'] || 
                      req.id || 
                      Math.random().toString(36).substring(2, 15);
    
    return this.child({ 
      requestId,
      method: req.method,
      url: req.url,
      category: 'request'
    });
  }

  // Log HTTP request (similar to Morgan but with structured logging)
  httpRequest(req: any, res: any, responseTime?: number): void {
    const statusCode = res.statusCode;
    const level = statusCode >= 500 ? 'error' : 
                 statusCode >= 400 ? 'warn' : 
                 'info';
    
    const requestId = req.headers?.['x-request-id'] || 
                     req.headers?.['request-id'] || 
                     req.id;
    
    const message = `${req.method} ${req.url} ${statusCode}${responseTime !== undefined ? ` - ${responseTime}ms` : ''}`;
    const params = {
      method: req.method,
      url: req.url,
      status: statusCode,
      requestId,
      ...(responseTime !== undefined ? { responseTime: `${responseTime}ms` } : {}),
      category: 'http'
    };
    
    // Call the appropriate log method based on level
    if (level === 'error') {
      this.error(params, message);
    } else if (level === 'warn') {
      this.warn(params, message);
    } else {
      this.info(params, message);
    }
  }

  // Performance tracking helper
  startTimer(): () => number {
    const start = typeof performance !== 'undefined' ? 
      performance.now() : 
      Date.now();
      
    return () => {
      const end = typeof performance !== 'undefined' ? 
        performance.now() : 
        Date.now();
      return Math.round(end - start);
    };
  }

  // Log with performance metrics
  perf(name: string, fn: () => any, extraParams: Record<string, any> = {}): any {
    const timer = this.startTimer();
    try {
      const result = fn();
      // Handle promise results
      if (result instanceof Promise) {
        return result.then(value => {
          const duration = timer();
          this.info({ 
            ...extraParams, 
            duration: `${duration}ms`, 
            category: 'perf',
            success: true 
          }, `${name} completed in ${duration}ms`);
          return value;
        }).catch(err => {
          const duration = timer();
          this.error({ 
            ...extraParams, 
            duration: `${duration}ms`, 
            category: 'perf',
            success: false,
            err 
          }, `${name} failed after ${duration}ms`);
          throw err;
        });
      }
      
      // Handle synchronous results
      const duration = timer();
      this.info({ 
        ...extraParams, 
        duration: `${duration}ms`, 
        category: 'perf',
        success: true 
      }, `${name} completed in ${duration}ms`);
      return result;
    } catch (err) {
      const duration = timer();
      this.error({ 
        ...extraParams, 
        duration: `${duration}ms`, 
        category: 'perf',
        success: false,
        err 
      }, `${name} failed after ${duration}ms`);
      throw err;
    }
  }
}

export const logger = new StructuredLogger();

// Usage examples (can be removed):
/*
// All these patterns now work:
logger.info('Simple message');
logger.info('Message with params', { userId: '123' });  // Original style
logger.info({ userId: '123' }, 'Message with params');  // New style
logger.info({ userId: '123', message: 'Inline message' });  // Object only

// Multiple arguments are automatically converted to params
logger.debug('Capturing event:', 'click', { x: 100, y: 200 });
// Becomes: { message: 'Capturing event:', params: { arg1: 'click', x: 100, y: 200 } }

logger.debug('User action:', 'login', 'success', { userId: '123' });
// Becomes: { message: 'User action:', params: { arg1: 'login', arg2: 'success', userId: '123' } }

// Error logging patterns
logger.error('Error occurred', new Error('Something went wrong'));
logger.error('Error with context', { userId: '123' }, new Error('Something went wrong'));
logger.error({ userId: '123' }, 'Error message', new Error('Something went wrong'));
logger.error('Failed to fetch', unknownError);  // Works with unknown error types
logger.error('API Error', { status: 500 }, someUnknownError);  // Works with params and unknown errors

// Child logger
const userLogger = logger.child({ userId: '123', context: 'user-service' });
userLogger.info('User action');
userLogger.info('User action with params', { action: 'login' });
userLogger.info({ action: 'login' }, 'User action with params');
*/

// Helper function to safely stringify potentially complex objects for logging
const safeStringify = (obj: any, depth = 2, maxLength = 1000): string => {
  let cache = new Set();
  
  // Helper function to detect and parse already stringified JSON
  const tryParseJSON = (value: any): any => {
    if (typeof value !== 'string') return value;
    
    // Check if the string looks like JSON
    if ((value.startsWith('{') && value.endsWith('}')) || 
        (value.startsWith('[') && value.endsWith(']'))) {
      try {
        return JSON.parse(value);
      } catch (e) {
        // Not valid JSON, return original
        return value;
      }
    }
    return value;
  };
  
  const safeReplacer = (key: string, value: any) => {
    // Try to parse stringified JSON first
    const parsedValue = tryParseJSON(value);
    
    // If the value was successfully parsed, use the parsed version
    if (parsedValue !== value) {
      return parsedValue;
    }
    
    // Handle circular references
    if (typeof parsedValue === 'object' && parsedValue !== null) {
      if (cache.has(parsedValue)) {
        return '[Circular Reference]';
      }
      cache.add(parsedValue);
      
      // Handle large arrays by truncating
      if (Array.isArray(parsedValue) && parsedValue.length > 10) {
        return [...parsedValue.slice(0, 10), `[...${parsedValue.length - 10} more items]`];
      }
    }
    
    // Handle very large strings
    if (typeof parsedValue === 'string' && parsedValue.length > 200) {
      return parsedValue.substring(0, 200) + '... [truncated]';
    }
    
    return parsedValue;
  };
  
  try {
    // First check if the entire obj is a stringified JSON
    const possiblyParsed = tryParseJSON(obj);
    const valueToStringify = possiblyParsed !== obj ? possiblyParsed : obj;
    
    const stringified = JSON.stringify(valueToStringify, safeReplacer, depth);
    
    // Truncate if the final string is too long
    if (stringified && stringified.length > maxLength) {
      return stringified.substring(0, maxLength) + '... [output truncated]';
    }
    
    return stringified || '[Could not stringify object]';
  } catch (err) {
    return '[Error during stringification]';
  } finally {
    cache.clear(); // Enable garbage collection
  }
};
